import * as React from 'react';
import { I18n } from 'react-redux-i18n';
import cx from 'classnames';

import { QuestionnaireLanguage } from '@common/dto/questionnaire/language';
import { QuestionnaireType } from '@common/dto/questionnaire/questionnaire-type';
import { SkipLogic } from '@common/components/skip-logic';
import { SkipLogicPredefinedControlType, SkipLogicProps } from '@common/components/skip-logic/skip-logic.types';
import { Flex } from '@common/components/Flex';
import { Button } from '@common/components/Button';

import { QuestionnaireHomeProps } from './questionnaire-home.types';
import { useQuestionnaireHomeController } from './questionnaire-home.controller';
import { TextField } from '../text-field';
import { FolderPicker } from '../folder-picker';
import { FieldPicker, FieldPickerProps } from '../field-picker';
import { Checkbox } from '../checkbox';
import { RadioGroup } from '../radio-group';
import { MODULE_NAME } from '@modules/fhq-design-tool/constants/shared';
import { Select } from '../select';
import { FormatPicker } from '../format-picker/format-picker';

import './questionnaire-home.styles.scss';

export const QuestionnaireHome: React.FC<QuestionnaireHomeProps> = (props) => {
  const { 
    foldersForGenericLink,
    familyPagesSkipLogicConditions,
    handleLanguageChange,
    handleQuestionnaireTypeChange,
    handleShowPrintAnswersChange,
    handleShowPedigreeChange,
    handleInviteDatasheetChange,
    handlePatientEmailChange,
    handlePatientLastNameChange,
    handlePatientFirstNameChange,
    handleGenericLinkChange,
    handleLogoUrlChange,
    handleExternalUrlChange,
    handleQuestionnaireNameChange,
    handleExitUrlChange,
    handleGenericLinkDefaultFolderChange,
    handleUseFamilyPagesSkipLogicChange,
    handleSkipLogicConditionAdd,
    handleSkipLogicChange,
    handleSetPedigreeSymbol,
    handleSetPedigreeSubtext,
    handleQuestionnaireStatusChange
  } = useQuestionnaireHomeController(props);
  const { errors, questionnaire, getDatasheetFolderFormats, getFoldersForDatasheetFormats, getFolderFields, getFolders, getFolderFormats, getFormatFolders } = props;
  return (
    <div className="FhqDesignToolQuestionnaireHome">
      <Flex>
        <TextField
          required
          errors={errors?.properties?.questionnaireName}
          margin={0}
          value={questionnaire.questionnaireName}
          onChange={handleQuestionnaireNameChange}
          metadata={{
            description: 'questionnaire-name',
          }}
          label={I18n.t('Questionnaire Name')}
        />
        <Button
          title={questionnaire.enabled ? I18n.t('Questionnaire Active') : I18n.t('Questionnaire Disabled')}
          variant="text"
          className={cx("FhqDesignToolQuestionnaireHome__status-action-btn", {
            ["FhqDesignToolQuestionnaireHome__status-action-btn--disabled"]: !questionnaire.enabled
          })}
          onClick={handleQuestionnaireStatusChange}
        >
          <i className="fa fa-power-off" />
        </Button>
      </Flex>

      <Select
        options={[
          { value: QuestionnaireLanguage.enUS, label: I18n.t("English (USA)") },
          { value: QuestionnaireLanguage.esES, label: I18n.t("Spanish (Latin America)") },
        ]}
        label={I18n.t("Questionnaire Language")}
        value={questionnaire.homeTab.localeCode}
        metadata={{
          description: "questionnaire-language",
        }}
        onChange={(value) => handleLanguageChange(value as QuestionnaireLanguage)}
      />

      <RadioGroup
        value={questionnaire.questionnaireType}
        options={[
          { id: QuestionnaireType.individual, name: I18n.t('Individual') },
          { id: QuestionnaireType.family, name: I18n.t('Family') },
        ]}
        onChange={handleQuestionnaireTypeChange as (val: string | number) => void}
        metadata={{
          description: 'questionnaire-type',
        }}
        label={I18n.t('Questionnaire Type')}
      />
      {questionnaire.questionnaireType === QuestionnaireType.family && (
        <>
          <RadioGroup
            margin="12px 0 12px 262px"
            value={`${questionnaire.homeTab.useFamilyPagesSkipLogic}`}
            options={[
              { id: "false", name: I18n.t("Always show family pages") },
              {
                id: "true",
                name: I18n.t(
                  "Show family pages only if condition below is true"
                ),
              },
            ]}
            onChange={
              handleUseFamilyPagesSkipLogicChange as (
                val: string | number
              ) => void
            }
            metadata={{
              description: "questionnaire-use-family-pages-skip-logic",
            }}
          />
          {questionnaire.homeTab.useFamilyPagesSkipLogic && (
            <SkipLogic
              moduleName={MODULE_NAME}
              className="FhqDesignToolQuestionnaireHome__familyPagesSkipLogic"
              conditions={familyPagesSkipLogicConditions}
              onRemove={handleSkipLogicChange}
              onChange={handleSkipLogicChange}
              onAdd={handleSkipLogicConditionAdd as SkipLogicProps['onAdd']}
              getFolders={getFolders}
              getFields={getFolderFields}
              controls={[
                { type: SkipLogicPredefinedControlType.moveUp, handler: handleSkipLogicChange },
                { type: SkipLogicPredefinedControlType.moveDown, handler: handleSkipLogicChange },
                { type: SkipLogicPredefinedControlType.add },
              ]} 
            />
          )}
        </>
      )}

      <TextField
        value={questionnaire.homeTab.genericLink || ''}
        onChange={handleGenericLinkChange}
        metadata={{
          description: 'generic-link',
        }}
        label={I18n.t('Generic Link')}
      />

      <FolderPicker
        required
        errors={errors?.properties?.genericLinkDefaultFolder}
        value={questionnaire.homeTab.genlinkTargetFolderId}
        onChange={handleGenericLinkDefaultFolderChange}
        metadata={{
          description: 'generic-link-default-folder',
        }}
        folders={foldersForGenericLink}
        label={I18n.t('Generic Link Default Folder')}
      />

      <FieldPicker 
        required
        errors={errors?.properties?.patientFirstName}
        withSearch={true}
        value={questionnaire.homeTab.firstNameFieldName}
        onChange={handlePatientFirstNameChange}
        metadata={{
          description: 'patient-first-name',
        }}
        getFields={getFolderFields}
        getFolders={getFolders}
        label={I18n.t('Patient First Name')}
      />

      <FieldPicker 
        required
        errors={errors?.properties?.patientLastName}
        withSearch={true}
        value={questionnaire.homeTab.lastNameFieldName}
        onChange={handlePatientLastNameChange}
        metadata={{
          description: 'patient-last-name',
        }}
        getFields={getFolderFields}
        getFolders={getFolders}
        label={I18n.t('Patient Last Name')}
      />

      <FieldPicker
        required 
        errors={errors?.properties?.patientEmail}
        withSearch={true}
        value={questionnaire.homeTab.patientEmailFieldName}
        onChange={handlePatientEmailChange}
        metadata={{
          description: 'patient-email-name',
        }}
        getFields={getFolderFields}
        getFolders={getFolders}
        label={I18n.t('Patient Email')}
      />

      <TextField
        errors={errors?.properties?.exitUrl}
        description={I18n.t("URL patient should be directed to after saving or submitting questionnaire")}
        value={questionnaire.homeTab.exitURL || ''}
        onChange={handleExitUrlChange}
        metadata={{
          description: 'exit-url',
        }}
        label={I18n.t('Exit URL')}
      />

      <TextField
        errors={errors?.properties?.externalUrl}
        description={I18n.t("Public facing URL that will replace the hostname/internal IP address of the server")}
        value={questionnaire.homeTab.externalURL || ''}
        onChange={handleExternalUrlChange}
        metadata={{
          description: 'external-url',
        }}
        label={I18n.t('External URL')}
      />

      <FieldPicker 
        clearable
        withSearch={false}
        value={questionnaire.homeTab.inviteDSFormatName}
        onChange={handleInviteDatasheetChange}
        metadata={{
          description: 'invite-datasheet',
        }}
        getFields={getDatasheetFolderFormats as FieldPickerProps['getFields']}
        getFolders={getFoldersForDatasheetFormats as FieldPickerProps['getFolders']}
        label={I18n.t('Invite Datasheet')}
        title={I18n.t('Datasheet Chooser')}
        placeholder={I18n.t('Select Datasheet')}
      />

      <Checkbox 
        value={questionnaire.homeTab.showPrintAnswers}
        onChange={handleShowPrintAnswersChange}
        metadata={{
          description: 'show-print-answers',
        }}
        label={I18n.t('Show Print Answers')}
      />

      <Checkbox 
        value={questionnaire.homeTab.showPedigree}
        onChange={handleShowPedigreeChange}
        metadata={{
          description: 'show-pedigree',
        }}
        label={I18n.t('Show Pedigree')}
      />

      <FormatPicker
        clearable={!!questionnaire.homeTab?.pedSymbolFormatName}
        value={questionnaire.homeTab?.pedSymbolFormatName ?? ""}
        metadata={{
          description: "pedigree-symbols-text",
        }}
        onChange={handleSetPedigreeSymbol}
        label={I18n.t("Pedigree Symbols Formats")}
        getFolderFormats={(queries) => getFolderFormats('defaultSymbolsFormatId', queries)}
        getFormatFolders={(queries) => getFormatFolders('defaultSymbolsFormatId', queries)}
      />

      <FormatPicker
        clearable={!!questionnaire.homeTab?.pedSubtextFormatName}
        value={questionnaire.homeTab?.pedSubtextFormatName ?? ""}
        metadata={{
          description: "pedigree-subtext-text",
        }}
        onChange={handleSetPedigreeSubtext}
        label={I18n.t("Pedigree Subtext Formats")}
        getFolderFormats={(queries) => getFolderFormats('defaultSubtextFormatId', queries)}
        getFormatFolders={(queries) => getFormatFolders('defaultSubtextFormatId', queries)}
      />

      <TextField
        errors={errors?.properties?.logoUrl}
        description={I18n.t("URL of logo to be displayed in questionnaire and on login screen")}
        value={questionnaire.homeTab.loginLogoURL || ''}
        onChange={handleLogoUrlChange}
        metadata={{
          description: 'logo-url',
        }}
        label={I18n.t('Logo URL')}
      />
    </div>
  );
};